import React from "react";
import { MPSProduct, MPSService, StatementResponse, StatementStatus } from "../../../types";
import ikhokhaLogo from "../../../assets/logo.svg";
import checkmarkIcon from "../../../assets/checkmark.svg";

import styles from "./index.module.css";
import centsToRand, {
  formatDatev2,
  getDurationText,
  getTotalAmountForMPSLineItem,
  getTotalAmountForMPSLineItemArray,
  getTotalVatAmountForMPSLineItemArray,
  getVatAmountForMPSLineItem,
  numberWithSpaces,
} from "../../../utils/utils";
import { Button } from "../../../components";

interface StatementMobileProps {
  statement: StatementResponse;
  products: MPSProduct[];
  services: MPSService[];
  onPayInvoice: Function;
  missingProductIds: string[];
  hasBlockingMissingItems: boolean;
  isStockAvailabilityLoading: boolean;
}

const StatementMobile = ({
  statement,
  onPayInvoice,
  products,
  services,
  missingProductIds,
  hasBlockingMissingItems,
  isStockAvailabilityLoading,
}: StatementMobileProps) => {
  const missingProductIdSet = new Set(missingProductIds);
  const isMissingProduct = (product: MPSProduct) => {
    const ids = [product.catalogueItemId];
    if (product.variant?.variantID) {
      ids.push(product.variant.variantID);
    }
    if (product.variant?.catalogueItemID) {
      ids.push(product.variant.catalogueItemID);
    }
    return ids.some(id => missingProductIdSet.has(id));
  };

  const invoiceReference = statement.statement.invoiceReference;
  const customerName = statement.statement.customer.customerName;

  const registeredName = statement.owner.companyName;
  const merchantEmail = statement.owner.email;
  const compiledMerchantAddress = `${statement.owner.address.street1}, ${statement.owner.address.city}, ${statement.owner.address.code}`;
  const merchantContactNumber = statement.owner.phoneNumber;
  const dueDate = statement.statement.dueDate;
  const additionalNotes = statement.statement.additionalNotes;
  const vat = statement.owner.vatNumber;
  const registerationNumber = statement.owner.companyRegistrationNumber;
  const shouldShowServicesTotal = products.length !== 0;
  const shouldShowProductsTotal = services.length !== 0;

  return (
    <>
      <div className={styles.header}>
        <div>
          <img src={ikhokhaLogo} alt="" />
        </div>
      </div>

      <div className={styles["card-container"]}>
        <div className={styles.card}>
          {statement.statement.businessLogo?.cropped && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <img
                style={{ height: "82px", objectFit: "contain" }}
                src={`${process.env.REACT_APP_BUSINESS_LOGO_IMAGES_CLOUDFRONT}/${statement.statement.businessLogo.cropped}`}
              />
            </div>
          )}
          {/* Heading */}
          <div className={styles["invoice-heading"]}>
            Invoice <br /> {invoiceReference} <br /> from {registeredName}
          </div>

          {/* Paid Banner */}
          {statement.statement.status === StatementStatus.PAID && (
            <div className={styles["paid-banner"]}>
              <img className={styles["tick-icon"]} src={checkmarkIcon} />
              This invoice is already paid
            </div>
          )}

          {hasBlockingMissingItems && (
            <div className={styles["unavailable-banner"]}>
              Some line items are no longer available. Please contact merchant.
            </div>
          )}
          <div className={styles["description"]}>
            Hi {customerName}, <br />
            Remember to make payment by clicking the <br />
            'Pay Invoice' button below.
          </div>

          {/* Products Table */}
          {products.length > 0 && (
            <>
              {" "}
              <p className={`${styles["table-heading"]} ${styles["mb-24"]} ${styles["mt-32"]}`}>Products</p>
              {products.map(product => {
                const variantName = product.variant ? Object.values(product.variant.variantValues).join("/") : undefined;
                const productUnavailable = isMissingProduct(product);
                return (
                  <div
                    key={product.variant ? product.variant.variantID : product.catalogueItemId}
                    className={`${styles["table-row"]} ${productUnavailable ? styles["unavailable-row"] : ""}`}
                  >
                    <>
                      <div>
                        <div className={styles["item-name"]}>{product.name}</div>
                        {variantName && <div className={styles["item-variant"]}>{variantName}</div>}
                        <div className={styles["item-qty"]}>Qty: {product.quantity}</div>
                      </div>

                      <div className={styles["text-right"]}>
                        <div className={styles["item-price"]} style={{ marginBottom: "2px" }}>{`R ${numberWithSpaces(
                          centsToRand(getTotalAmountForMPSLineItem(product)),
                        )}`}</div>
                        <div>
                          {product.vat ? `(VAT: R ${numberWithSpaces(centsToRand(getVatAmountForMPSLineItem(product)))})` : ""}
                        </div>
                      </div>
                    </>
                  </div>
                );
              })}
              {!shouldShowProductsTotal && <div className={styles["total-divider"]}></div>}
              {/* Product's total */}
              {shouldShowProductsTotal && (
                <div className={styles["total-row"]}>
                  <div>R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray(products)))}</div>
                  <div>VAT: R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray(products)))}</div>
                </div>
              )}
            </>
          )}

          {/* Services Table */}
          {services.length > 0 && (
            <>
              <p className={`${styles["table-heading"]} ${styles["mt-40"]}`}>Services</p>
              {services.map(service => {
                return (
                  <div key={service.catalogueItemId} className={styles["table-row"]}>
                    <>
                      <div>
                        <div className={styles["item-name"]}>{service.name}</div>
                        <div className={styles["item-qty"]}>
                          Qty: {service.quantity} - {getDurationText(service.duration)}
                        </div>
                      </div>

                      <div className={styles["text-right"]}>
                        <div>{`R ${numberWithSpaces(centsToRand(getTotalAmountForMPSLineItem(service)))}`}</div>
                        <div>
                          {service.vat ? `(VAT: R ${numberWithSpaces(centsToRand(getVatAmountForMPSLineItem(service)))})` : ""}
                        </div>
                      </div>
                    </>
                  </div>
                );
              })}
              {!shouldShowServicesTotal && <div className={styles["total-divider"]}></div>}
              {/* Services's total */}
              {shouldShowServicesTotal && (
                <div className={styles["total-row"]}>
                  <div>R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray(services)))}</div>
                  <div>VAT: R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray(services)))}</div>
                </div>
              )}
            </>
          )}

          {/* Overall total */}
          <div className={`${styles["total-vat"]} ${styles["mt-24"]}`}>
            <span>VAT</span>
            <span> R {numberWithSpaces(centsToRand(getTotalVatAmountForMPSLineItemArray([...services, ...products])))}</span>
          </div>
          <div className={styles["total-amount"]}>
            <span>Invoice Total</span>
            <span>R {numberWithSpaces(centsToRand(getTotalAmountForMPSLineItemArray([...services, ...products])))}</span>
          </div>

          <div className={styles["mt-24"]}>
            {statement.statement.status !== StatementStatus.PAID && (
              <Button
                type="button"
                label="Pay Invoice"
                size="medium"
                disabled={hasBlockingMissingItems || isStockAvailabilityLoading}
                onClick={() => {
                  onPayInvoice(statement.statement.id, statement.statement.documentType);
                }}
              />
            )}
          </div>

          {/* Invoice Details */}
          <div className={styles["mt-32"]}>
            <div className={styles["details-label"]}>Invoice number</div>
            <div className={styles["details-item"]}>{invoiceReference}</div>
          </div>

          <div className={styles["mt-24"]}>
            <div className={styles["details-label"]}>Due date</div>
            <div className={styles["details-item"]}>{formatDatev2(dueDate)}</div>
          </div>

          {additionalNotes && (
            <div className={styles["mt-24"]}>
              <div className={styles["details-label"]}>Additional notes</div>
              <div className={styles["details-item"]}>{additionalNotes}</div>
            </div>
          )}

          <div className={`${styles["mt-32"]} ${styles["mb-24"]} ${styles["merchant-info-heading"]}`}>Merchant Information</div>

          <div>
            <div className={styles["details-label"]}>Trading Name</div>
            <div className={styles["merchant-detail-item"]}>{registeredName}</div>
          </div>

          <div className={styles["mt-24"]}>
            <div className={styles["details-label"]}>Email</div>
            <div className={styles["merchant-detail-item"]}>{merchantEmail}</div>
          </div>

          <div className={styles["mt-24"]}>
            <div className={styles["details-label"]}>Number</div>
            <div className={styles["merchant-detail-item"]}>{merchantContactNumber}</div>
          </div>

          <div className={styles["mt-24"]}>
            <div className={styles["details-label"]}>Trading Address</div>
            <div className={styles["merchant-detail-item"]}>{compiledMerchantAddress}</div>
          </div>

          {vat && (
            <div className={styles["mt-24"]}>
              <div className={styles["details-label"]}>VAT</div>
              <div className={styles["merchant-detail-item"]}>{vat}</div>
            </div>
          )}

          {registerationNumber && (
            <div className={styles["mt-24"]}>
              <div className={styles["details-label"]}>Reg No.</div>
              <div className={styles["merchant-detail-item"]}>{registerationNumber}</div>
            </div>
          )}

          <div className={styles.divider}></div>

          <p>
            If you need help, please call our{" "}
            <span className={styles["roboto-medium"]} style={{ marginRight: "6px" }}>
              Support Team
            </span>
            on{" "}
            <a href="tel:087-222-7000" className={styles["phone-text"]}>
              {" "}
              087 222 7000
            </a>{" "}
            or email us at{" "}
            <a href="mailto:support@ikhokha.com" className={styles["email-text"]}>
              support@ikhokha.com
            </a>
          </p>

          <p className={styles["roboto-medium"]}>The iKhokha Team</p>
        </div>
      </div>
    </>
  );
};
export default StatementMobile;
